import { CatmullRomCurve3, type PerspectiveCamera, Vector3 } from 'three';
import type { CameraMode, TunableSpec } from './cameraMode';

export interface RailsConfig {
  /** Waypoints in world space; camera moves along the spline through them. */
  path: Vector3[];
  /** Either a fixed point to look at, or 'ahead' (look forward along the path). */
  lookAt: Vector3 | 'ahead';
  closed?: boolean;
  /** Wheel deltaY → t multiplier. Smaller = slower. */
  speed?: number;
  /** A1 — pointer-drag-X → t multiplier (the touch/drag rail scrub). Smaller =
   *  slower; negative flips the drag direction. John tunes by eye. */
  dragSpeed?: number;
  /** A1 — defer to an active cloth grab so a drag that began on cloth never
   *  doubles as a rail move (cloth-grab keeps pointer precedence). */
  clothGrabbing?: () => boolean;
  initialT?: number;
}

const TUNABLES: TunableSpec[] = [
  { key: 't', label: 'rails t', min: 0, max: 1, step: 0.001 },
  { key: 'speed', label: 'wheel speed', min: 0.0001, max: 0.005, step: 0.0001 },
  { key: 'dragSpeed', label: 'drag speed', min: 0.0002, max: 0.006, step: 0.0002 },
  { key: 'smoothing', label: 'smoothing', min: 0.01, max: 1, step: 0.01 },
];

// A1 — a pointer that travels past this many px engages a rail drag; under it,
// the gesture stays a tap (which selects/plays a hero via PointerInteraction).
const DRAG_THRESHOLD_PX = 6;

export class RailsMode implements CameraMode {
  private curve: CatmullRomCurve3;
  private _t: number;
  private targetT: number;
  speed: number;
  dragSpeed: number;
  smoothing = 0.1;
  private readonly closed: boolean;
  // A1 — pointer-drag state (touch/mouse rail scrub).
  private dragPointerId = -1;
  private dragDownX = 0;
  private dragDownY = 0;
  private dragLastX = 0;
  private dragging = false;
  // Active = between init() and dispose(). Multiple RailsMode instances share
  // one camera (interior-walk + interior-orbit + freeform), so an inactive
  // mode must NOT mutate camera.position — otherwise applying saved tunables
  // (e.g. `t: 0` on the inactive interior modes at boot) would teleport the
  // active mode's camera onto the rails. Mirrors FreeformMode's
  // `if (this.controls)` guard around its setters.
  private active = false;

  constructor(
    private camera: PerspectiveCamera,
    private domElement: HTMLElement,
    private config: RailsConfig,
  ) {
    this.curve = new CatmullRomCurve3(
      config.path.map((p) => p.clone()),
      config.closed ?? false,
    );
    this.speed = config.speed ?? 0.0006;
    this.dragSpeed = config.dragSpeed ?? 0.0016;
    this.closed = config.closed ?? false;
    this._t = config.initialT ?? 0;
    this.targetT = this._t;
  }

  get t(): number { return this._t; }
  set t(v: number) {
    this._t = this.clampT(v);
    this.targetT = this._t;
    // Only drive the shared camera when we're the active mode. Setting `t`
    // on a parked mode just stashes the value for next init().
    if (this.active) this.applyAt(this._t);
  }

  init(): void {
    this.active = true;
    this.applyAt(this._t);
    this.domElement.addEventListener('wheel', this.handleWheel, { passive: false });
    // A1 — touch/mouse rail scrub (interior-orbit was wheel-only → dead on touch).
    this.domElement.addEventListener('pointerdown', this.handlePointerDown);
    this.domElement.addEventListener('pointermove', this.handlePointerMove);
    this.domElement.addEventListener('pointerup', this.handlePointerUp);
    this.domElement.addEventListener('pointercancel', this.handlePointerUp);
  }

  update(): void {
    if (!this.active) return;
    let diff = this.targetT - this._t;
    // For a closed loop, wrap diff into the shorter direction so the camera
    // doesn't lerp the long way across the seam (e.g. 0.95 -> 0.05 should
    // travel +0.10, not -0.90).
    if (this.closed) {
      if (diff > 0.5) diff -= 1;
      else if (diff < -0.5) diff += 1;
    }
    if (Math.abs(diff) > 0.00005) {
      this._t = this.clampT(this._t + diff * this.smoothing);
      this.applyAt(this._t);
    }
  }

  dispose(): void {
    this.active = false;
    this.domElement.removeEventListener('wheel', this.handleWheel);
    this.domElement.removeEventListener('pointerdown', this.handlePointerDown);
    this.domElement.removeEventListener('pointermove', this.handlePointerMove);
    this.domElement.removeEventListener('pointerup', this.handlePointerUp);
    this.domElement.removeEventListener('pointercancel', this.handlePointerUp);
    this.dragging = false;
    this.dragPointerId = -1;
  }

  getTunables() {
    return { target: this, specs: TUNABLES };
  }

  private clampT(v: number): number {
    if (this.closed) return ((v % 1) + 1) % 1;
    return Math.max(0, Math.min(1, v));
  }

  private handleWheel = (e: WheelEvent) => {
    e.preventDefault();
    this.targetT = this.clampT(this.targetT + e.deltaY * this.speed);
  };

  // A1 — pointer-drag rail scrub. A tap (under the threshold) is left alone so
  // PointerInteraction still selects/plays the hero under it; a drag past the
  // threshold scrubs `targetT` (the existing smoothing eases `_t` toward it).
  // Cloth-grab keeps precedence: we never start (or we abandon) a drag while a
  // cloth grab is live (its capture-phase handler owns the gesture).
  private handlePointerDown = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse' && e.button !== 0) return; // primary button only
    if (this.config.clothGrabbing?.()) return;
    this.dragPointerId = e.pointerId;
    this.dragging = false;
    this.dragDownX = e.clientX;
    this.dragDownY = e.clientY;
    this.dragLastX = e.clientX;
  };

  private handlePointerMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.dragPointerId) return;
    // A cloth grab can engage AFTER our pointerdown (capture phase) — yield to it.
    if (this.config.clothGrabbing?.()) { this.dragPointerId = -1; this.dragging = false; return; }
    if (!this.dragging) {
      if (Math.hypot(e.clientX - this.dragDownX, e.clientY - this.dragDownY) < DRAG_THRESHOLD_PX) return;
      this.dragging = true;
      try { this.domElement.setPointerCapture(e.pointerId); } catch { /* focus race — safe to ignore */ }
    }
    const dx = e.clientX - this.dragLastX;
    this.dragLastX = e.clientX;
    e.preventDefault();
    this.targetT = this.clampT(this.targetT + dx * this.dragSpeed);
  };

  private handlePointerUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.dragPointerId) return;
    try { this.domElement.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    this.dragPointerId = -1;
    this.dragging = false;
  };

  private applyAt(t: number): void {
    const tt = this.clampT(t);
    const pos = this.curve.getPointAt(tt);
    this.camera.position.copy(pos);

    if (this.config.lookAt === 'ahead') {
      const lookT = this.closed ? this.clampT(tt + 0.01) : Math.min(1, tt + 0.01);
      const ahead = this.curve.getPointAt(lookT);
      if (ahead.distanceToSquared(pos) > 1e-6) this.camera.lookAt(ahead);
    } else {
      this.camera.lookAt(this.config.lookAt);
    }
  }
}
